/**
 * TimeEntryWidget
 *
 * Pattern: DetailPanel (pannello laterale destro, mobile = full screen)
 * Unified with all other widgets in the platform for consistent 50% width layout.
 */

import { useState } from 'react';
import { Clock } from 'lucide-react';
import { useProjectsStore } from '@business-assistant/shared';
import { DetailPanel, DetailSection, DetailActions } from '../common/base';
import { useTranslation } from '../../hooks/useTranslation';
import { Button, TextAreaField, SelectField } from '../common/forms';

interface TimeEntryFormProps {
  projectId: string;
  onClose: () => void;
  onSave: (data: TimeEntryFormData) => void;
}

export interface TimeEntryFormData {
  description: string;
  minutes: number;
  at: string;
  taskId?: string;
  userId: string;
  userName: string;
  billable: boolean;
  hourlyRate?: number;
}

export function TimeEntryWidget({ projectId, onClose, onSave }: TimeEntryFormProps) {
  const { t } = useTranslation();
  const { getProjectTasks } = useProjectsStore();
  const tasks = getProjectTasks(projectId);

  const [formData, setFormData] = useState<TimeEntryFormData>({
    description: '',
    minutes: 60, // Default 1 hour
    at: new Date().toISOString().split('T')[0], // Today
    taskId: undefined,
    userId: 'usr-current', // TODO: Get from auth
    userName: 'Current User', // TODO: Get from auth
    billable: true,
    hourlyRate: undefined,
  });

  const [hours, setHours] = useState(1);
  const [mins, setMins] = useState(0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const totalMinutes = hours * 60 + mins;
    onSave({
      ...formData,
      minutes: totalMinutes,
    });
  };

  const handleHoursChange = (value: number) => {
    setHours(Math.max(0, value));
    setFormData({ ...formData, minutes: value * 60 + mins });
  };

  const handleMinsChange = (value: number) => {
    setMins(Math.max(0, Math.min(59, value)));
    setFormData({ ...formData, minutes: hours * 60 + value });
  };

  return (
    <DetailPanel
      title={t('timeEntry.logTime')}
      icon={<Clock size={20} style={{ color: '#06b6d4' }} />}
      onClose={onClose}
      accentColor="#06b6d4"
      width="50%"
    >
      <form onSubmit={handleSubmit}>
        {/* Time Duration */}
        <DetailSection title={t('timeEntry.durationRequired')}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                {t('timeEntry.hours')}
              </label>
              <input
                type="number"
                min="0"
                value={hours}
                onChange={(e) => handleHoursChange(parseInt(e.target.value) || 0)}
                required
                style={{
                  width: '100%', padding: '0.625rem', border: '1px solid var(--line)',
                  borderRadius: '0.375rem', fontSize: '0.875rem',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                {t('timeEntry.minutes')}
              </label>
              <input
                type="number"
                min="0"
                max="59"
                value={mins}
                onChange={(e) => handleMinsChange(parseInt(e.target.value) || 0)}
                style={{
                  width: '100%', padding: '0.625rem', border: '1px solid var(--line)',
                  borderRadius: '0.375rem', fontSize: '0.875rem',
                }}
              />
            </div>
          </div>
          <div style={{ marginTop: '0.375rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {t('timeEntry.totalMinutes', { count: hours * 60 + mins })}
          </div>
        </DetailSection>

        {/* Date */}
        <DetailSection title={t('timeEntry.dateRequired')}>
          <input
            type="date"
            value={formData.at}
            onChange={(e) => setFormData({ ...formData, at: e.target.value })}
            required
            style={{
              width: '100%', padding: '0.625rem', border: '1px solid var(--line)',
              borderRadius: '0.375rem', fontSize: '0.875rem',
            }}
          />
        </DetailSection>

        {/* Task (optional) */}
        <DetailSection title={t('timeEntry.linkedTask')}>
          <select
            value={formData.taskId || ''}
            onChange={(e) => setFormData({ ...formData, taskId: e.target.value || undefined })}
            style={{
              width: '100%', padding: '0.625rem', border: '1px solid var(--line)',
              borderRadius: '0.375rem', fontSize: '0.875rem', background: 'white',
            }}
          >
            <option value="">{t('timeEntry.noTask')}</option>
            {tasks.map((task) => (
              <option key={task.id} value={task.id}>
                {task.title}
              </option>
            ))}
          </select>
        </DetailSection>

        {/* Description */}
        <DetailSection title={t('timeEntry.description')}>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t('timeEntry.whatDidYouDo')}
            rows={3}
            style={{
              width: '100%', padding: '0.625rem', border: '1px solid var(--line)',
              borderRadius: '0.375rem', fontSize: '0.875rem', resize: 'vertical',
            }}
          />
        </DetailSection>

        {/* Billable */}
        <DetailSection title={t('timeEntry.billingOptions')}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              cursor: 'pointer', fontSize: '0.875rem',
            }}>
              <input
                type="checkbox"
                checked={formData.billable}
                onChange={(e) => setFormData({ ...formData, billable: e.target.checked })}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 500 }}>{t('timeEntry.billableTime')}</span>
            </label>
          </div>

          {/* Hourly Rate (if billable) */}
          {formData.billable && (
            <div>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.375rem', fontSize: '0.875rem' }}>
                {t('timeEntry.hourlyRate')}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.hourlyRate || ''}
                onChange={(e) => setFormData({ ...formData, hourlyRate: parseFloat(e.target.value) || undefined })}
                placeholder={t('timeEntry.hourlyRatePlaceholder')}
                style={{
                  width: '100%', padding: '0.625rem', border: '1px solid var(--line)',
                  borderRadius: '0.375rem', fontSize: '0.875rem',
                }}
              />
              {formData.hourlyRate && (
                <div style={{ marginTop: '0.375rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {t('timeEntry.estimatedValue', { value: ((formData.hourlyRate * (hours * 60 + mins)) / 60).toFixed(2) })}
                </div>
              )}
            </div>
          )}
        </DetailSection>

        {/* Actions */}
        <DetailActions>
          <Button variant="secondary" type="button" onClick={onClose}>
            {t('timeEntry.cancel')}
          </Button>
          <Button variant="primary" type="submit">
            {t('timeEntry.logTimeButton')}
          </Button>
        </DetailActions>
      </form>
    </DetailPanel>
  );
}
